import { useEffect, useRef, useState } from "react";

const MAX_SECONDS = 60;

function micError(e) {
  const s = String(e?.name || e?.message || e || "");
  if (!window.isSecureContext) return "Le micro nécessite une connexion sécurisée (HTTPS).";
  if (/NotAllowed|Permission|denied/i.test(s)) return "Accès au micro refusé. Autorisez le micro dans les réglages du navigateur. Vous pouvez continuer à écrire.";
  if (/NotFound|not found|Overconstrained/i.test(s)) return "Aucun micro détecté sur cet appareil. Vous pouvez continuer à écrire.";
  if (/NotReadable|in use/i.test(s)) return "Le micro est déjà utilisé par une autre application.";
  return `Impossible d'utiliser le micro : ${s}`;
}

function pickMime() {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return types.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
}

export function useRecorder(onAudio) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const rec = useRef(null);
  const timer = useRef(null);
  const cbRef = useRef(onAudio);
  cbRef.current = onAudio;

  const cleanup = () => {
    clearInterval(timer.current);
    rec.current?.stream?.getTracks().forEach((t) => t.stop());
  };
  useEffect(() => () => { if (rec.current?.state === "recording") rec.current.stop(); cleanup(); }, []);

  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); };

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError(window.isSecureContext ? "Votre navigateur ne permet pas l'enregistrement audio. Vous pouvez continuer à écrire." : micError());
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        cleanup();
        setRecording(false);
        const type = r.mimeType || mime || "audio/webm";
        cbRef.current(new Blob(chunks, { type }), type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm");
      };
      rec.current = r;
      r.start();
      setSeconds(0);
      setRecording(true);
      const t0 = Date.now();
      timer.current = setInterval(() => {
        const s = Math.floor((Date.now() - t0) / 1000);
        setSeconds(s);
        if (s >= MAX_SECONDS) stop();
      }, 250);
    } catch (e) {
      setError(micError(e));
    }
  };

  return { recording, seconds, error, setError, start, stop };
}
